import React, { useMemo } from 'react';

import { InlineNotification } from '@paragon/notification-tools';
import { naturalSortByProperty, notNullish } from '@paragon/utilities';

import { getAnalysisSetGuidsNotInProjectOrAtDifferentVersionForActiveProductionGroupBatchesAndBundlesTab } from '../../../selectors/analysisSets';
import { getLoadedTrussesByAnalysisSetGuid } from '../../../selectors/trusses';
import { useAppSelector } from '../../hooks';

/**
 * This warning exists to let users know about trusses that are in the production group but that no longer exist in the
 * project, either because they were deleted from the project, convert to settings was used, or something else. We want
 * the user to know about these so that they can take action to make sure that the wrong trusses are not built.
 */
export function ComponentsDifferFromProjectError() {
  const analysisSetGuidsNotInProjectForActiveProductionGroup = useAppSelector(
    getAnalysisSetGuidsNotInProjectOrAtDifferentVersionForActiveProductionGroupBatchesAndBundlesTab,
  );
  const loadedTrussesByAnalysisSetGuid = useAppSelector(getLoadedTrussesByAnalysisSetGuid);
  const trussNamesNotInProject = useMemo(
    () =>
      naturalSortByProperty(
        analysisSetGuidsNotInProjectForActiveProductionGroup
          .map((analysisSetGuid) => loadedTrussesByAnalysisSetGuid[analysisSetGuid]?.name)
          .filter(notNullish),
        (x) => x,
      ).join(', '),
    [analysisSetGuidsNotInProjectForActiveProductionGroup, loadedTrussesByAnalysisSetGuid],
  );

  if (analysisSetGuidsNotInProjectForActiveProductionGroup.length === 0) return null;

  return (
    <InlineNotification type="error">
      Some trusses in this production group do not exist in the project, or exist as a different version:{' '}
      {trussNamesNotInProject}. Remove or replace them so that trusses are not built incorrectly.
    </InlineNotification>
  );
}
